import { TabPanel } from "../TabComponents";
import { useTBContext } from "../../Contexts/useTBStates";
import { Box, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material";
import { EventItemDisplay } from "../EventItemDisplay";
import { RoundItemDisplay } from "../RoundItemDisplay";
import { StablefordPointsLayout } from "../Point Systems/StablefordPoints";
import { StyledAutocompleteInput } from "../../../shared/Components/StyledAutocompleteInput";
import { MatchPlayPointsLayout } from "../Point Systems/MatchPlayPoint";
import { NassauPointsLayout } from "../Point Systems/NassauPoints";
import { MixedPlayFormatsLayout } from "../Point Systems/MixedPlayFormats";
import { HandicapDisplay } from "../Handicap Items/HandicapDisplay";
import type { RoundDataLayout } from "@moment-golf/types";
import { ROUND_LOCKED_FIELDS, HDCP_LOCKED_FIELDS } from "../Functions/cascadeLockedFields";
import { EventDetailsSectionCard } from "./EventDetailsSectionCard";

interface RoundDetailsTabProps {
    appDims: any;
    compHeights: any;
    roundDataLayout: RoundDataLayout;
    setRoundDataLayout: React.Dispatch<React.SetStateAction<RoundDataLayout>>;
    UpdateRoundState: (roundIndex: number, keyName: string, keyValue: any, keyIDName?: string, keyIDValue?: any) => void;
    roundLocked?: boolean;
}

export const RoundDetailsTab = ({ appDims, compHeights, roundDataLayout, UpdateRoundState, roundLocked }: RoundDetailsTabProps) => {

    const { viewEvent, roundValue, factor, eventTabValue, appTheme, editMode, optionsLoaded, setViewEvent, viewGroup, groupData } = useTBContext();

    const currentRound = viewEvent?.rounds?.[roundValue - 1];
    const pointSystemsOpt = optionsLoaded?.find((item: any) => item.keyName === 'PointSystems') as { options?: Array<{ type: string }> } | undefined;

    const isInSeries = (viewEvent?.eventGroupIDs?.length ?? 0) > 0;

    // viewGroup is set when the event was opened from inside the collection editor.
    // When the event was opened directly from the events list, viewGroup is null —
    // fall back to looking up the collection in the already-loaded groupData cache.
    const activeGroup = viewGroup ?? groupData.find(g => g._id === viewEvent?.eventGroupIDs?.[0]) ?? null;
    const filteredPointSystems = pointSystemsOpt?.options?.filter(
        (opt: any) => opt.type !== 'Series' || isInSeries
    ) ?? [];

    const detailItems = roundDataLayout.filter((item: any) => item.section === "Round Details");
    const maxBlock = Math.max(0, ...detailItems.map((item: any) => item.block ?? 0));
    const columnCount = appDims?.appWidth > 1200 ? 3 : appDims?.appWidth > 750 ? 2 : 1;

    const updatePointSystem = (value: any) => {
        if (!viewEvent?.rounds?.[roundValue - 1]) return;
        const newViewEvent = JSON.parse(JSON.stringify(viewEvent));
        newViewEvent.rounds[roundValue - 1].pointSystem = value;
        newViewEvent.rounds[roundValue - 1].holePoints = [];
        setViewEvent(newViewEvent);
    };

    if (!viewEvent || !currentRound) return null;

    return (
        <TabPanel value={eventTabValue} index={4} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable>
            <Box
                id="tab-panel-round-details-content"
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    minHeight: 0,
                    flex: 1,
                    boxSizing: 'border-box',
                    p: 1.5,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    columnCount,
                    columnGap: 2.5,
                    '& > *': {
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                    },
                }}
            >
                {[...Array(maxBlock + 1)].map((_, blockIndex) => {
                    const blockName = detailItems.find((item: any) => item.block === blockIndex)?.blockName ?? `Section ${blockIndex}`;
                    const blockFields = detailItems.filter((item: any) => item.block === blockIndex);

                    return (
                        <EventDetailsSectionCard key={`round-details-block-${blockIndex}`} title={blockName}>
                            {blockFields.map((item: any, itemIndex: number) => {
                                if (item.eventLevel) {
                                    return (
                                        <EventItemDisplay
                                            key={`${item.field}-${itemIndex}`}
                                            orient="row"
                                            label={item.label}
                                            keyName={item.field}
                                            labelField={item.labelField}
                                            optionsKeyName={item.optionsKeyName}
                                            locked={item.locked}
                                        />
                                    );
                                }

                                if (item.blockName === 'Handicap Usage') {
                                    return (
                                        <HandicapDisplay
                                            key={`hdcp-${itemIndex}`}
                                            level="round"
                                            keyName={item.field}
                                            locked={roundLocked && HDCP_LOCKED_FIELDS.includes(item.field)}
                                            orient="row"
                                        />
                                    );
                                }

                                if (
                                    (item.field === 'matchRegions' || item.field === 'matchRegionRounds')
                                    && !(currentRound.useBracket && (item.field === 'matchRegions' || item.field === 'matchRegionRounds'))
                                ) {
                                    return null;
                                }

                                return (
                                    <RoundItemDisplay
                                        key={`${item.field}-${itemIndex}`}
                                        orient="row"
                                        label={item.label}
                                        keyName={item.field}
                                        labelField={item.labelField}
                                        optionsKeyName={item.optionsKeyName}
                                        locked={item.locked || (roundLocked && ROUND_LOCKED_FIELDS.includes(item.field))}
                                        handleUpdate={UpdateRoundState}
                                    />
                                );
                            })}
                        </EventDetailsSectionCard>
                    );
                })}

                {currentRound.usePoints && (
                    <EventDetailsSectionCard title="Points System">
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{
                                width: '100%',
                                maxWidth: '100%',
                                minWidth: 0,
                                boxSizing: 'border-box',
                            }}
                        >
                            <Typography
                                flex={2}
                                textAlign="left"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 14 * factor,
                                    lineHeight: 1.4,
                                    minHeight: 24 * factor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: 0,
                                    flexShrink: 1,
                                }}
                            >
                                Points System:
                            </Typography>
                            <Box sx={{ flex: 3, minWidth: 0 }}>
                                <StyledAutocompleteInput
                                    label="Points System"
                                    disabled={!editMode}
                                    factor={factor}
                                    appTheme={appTheme}
                                    width="100%"
                                    value={filteredPointSystems.find((item: any) => item.type === currentRound.pointSystem) || (currentRound.pointSystem ? { type: currentRound.pointSystem } : null)}
                                    getOptionLabel={(option: any) => option.type}
                                    isOptionEqualToValue={(option: any, value: any) => option?.type === value?.type}
                                    options={filteredPointSystems}
                                    onChange={(_event, value) => {
                                        if (value?.type) updatePointSystem(value.type);
                                    }}
                                />
                            </Box>
                        </Stack>
                        {currentRound.pointSystem === 'Series' && (
                            <FormControlLabel
                                label="Use Series Points for Teams"
                                control={
                                    <Switch
                                        checked={Boolean(currentRound.useSeriesPointsForTeams)}
                                        disabled={!editMode}
                                        onChange={(e) =>
                                            UpdateRoundState(roundValue - 1, 'useSeriesPointsForTeams', e.target.checked)
                                        }
                                    />
                                }
                            />
                        )}
                        {currentRound.pointSystem === 'Series' && activeGroup?.seriesPoints?.allowMultiplier && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                                <Typography
                                    flex={2}
                                    textAlign="left"
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: 14 * factor,
                                        lineHeight: 1.4,
                                        minHeight: 24 * factor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        minWidth: 0,
                                        flexShrink: 1,
                                    }}
                                >
                                    Series Multiplier:
                                </Typography>
                                <Box sx={{ flex: 3, minWidth: 0 }}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        fullWidth
                                        disabled={!editMode}
                                        value={currentRound.seriesPointsMultiplier ?? 1}
                                        inputProps={{ step: 0.01, min: 0.01 }}
                                        sx={{
                                            '& .MuiInputBase-input': { fontSize: 14 * factor, padding: '6px 10px' },
                                        }}
                                        onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            UpdateRoundState(roundValue - 1, 'seriesPointsMultiplier', isNaN(val) ? 1 : val);
                                        }}
                                    />
                                </Box>
                            </Stack>
                        )}
                        {currentRound.pointSystem === 'Stableford' && (
                            <StablefordPointsLayout />
                        )}
                        {currentRound.pointSystem === 'Match Play' && (
                            <MatchPlayPointsLayout />
                        )}
                        {currentRound.pointSystem === 'Nassau' && (
                            <NassauPointsLayout />
                        )}
                    </EventDetailsSectionCard>
                )}

                {currentRound.playFormat === 'Mixed' && (
                    <EventDetailsSectionCard title="Play Formats">
                        <MixedPlayFormatsLayout />
                    </EventDetailsSectionCard>
                )}
            </Box>
        </TabPanel>
    );
};
